import {useEffect, useRef} from 'react';
import {fetchServerStatusRequest} from 'APIs/letrusAPI/serverStatus';

const HEARTBEAT_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const COUNTDOWN_TICK_MS = 1000; // 1 second

function parseDateTimeMs(dateTime: string | undefined): number | null {
  if (!dateTime) {
    return null;
  }

  const parsed = new Date(dateTime).getTime();

  return Number.isFinite(parsed) ? parsed : null;
}

export interface UseFocusModeTimerParams {
  isActive: boolean;
  startedAt: string;
  effectiveTimeSeconds: number;
  completionDeadlineDatetime: string | undefined;
  endDatetime: string | undefined;
  setRemainingSeconds(seconds: number): void;
  setServerOffset(offsetMs: number): void;
  setIsExpired(expired: boolean): void;
  setIsInitializingTimer?(initializing: boolean): void;
}

/**
 * Calculates the remaining time in seconds, taking the minimum between:
 * - Time remaining based on the timer duration (effective_time - elapsed)
 * - Time remaining until the earliest absolute deadline (completion_deadline and end_datetime)
 */
function getRemainingSeconds(
  effectiveTimeSeconds: number,
  startedAt: string,
  serverNowMs: number,
  completionDeadlineDatetime: string | undefined,
  endDatetime: string | undefined,
): number {
  const startedMs = parseDateTimeMs(startedAt);
  if (startedMs === null) {
    return 0;
  }

  const elapsedMs = serverNowMs - startedMs;
  const remainingByTimer = effectiveTimeSeconds - elapsedMs / 1000;

  const absoluteDeadlines = [completionDeadlineDatetime, endDatetime]
    .map(parseDateTimeMs)
    .filter((deadlineMs): deadlineMs is number => deadlineMs !== null);

  const remainingByDeadline =
    absoluteDeadlines.length > 0
      ? Math.min(
          ...absoluteDeadlines.map(
            (deadlineMs) => (deadlineMs - serverNowMs) / 1000,
          ),
        )
      : Infinity;

  return Math.min(remainingByTimer, remainingByDeadline);
}

/**
 * useFocusModeTimer
 *
 * Manages the countdown timer for Focus Mode.
 *
 * - On mount (when isActive && startedAt): fetches server time to calculate
 *   the initial serverOffset, then starts a 1s countdown tick using a ref
 *   to avoid re-rendering the Provider tree every second.
 * - Every 10 minutes: heartbeat to recalibrate serverOffset.
 * - When remaining time reaches 0: calls setIsExpired(true).
 * - Cleans up all intervals on unmount or when isActive turns false.
 *
 * Intentionally does NOT call useFocusMode() to avoid circular dependency.
 * Receives all needed values as params and writes back via setters.
 */
function useFocusModeTimer({
  isActive,
  startedAt,
  effectiveTimeSeconds,
  completionDeadlineDatetime,
  endDatetime,
  setRemainingSeconds,
  setServerOffset,
  setIsExpired,
  setIsInitializingTimer,
}: UseFocusModeTimerParams): void {
  const serverOffsetRef = useRef(0);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);

  const {trigger: triggerServerStatus} = fetchServerStatusRequest();
  const triggerServerStatusRef = useRef(triggerServerStatus);
  triggerServerStatusRef.current = triggerServerStatus;

  useEffect(() => {
    let cancelled = false;

    function clearTimers() {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    }

    function startCountdown(initialRemainingSeconds: number) {
      let remainingMs = initialRemainingSeconds * 1000;

      countdownRef.current = setInterval(() => {
        remainingMs -= COUNTDOWN_TICK_MS;

        const remainingSeconds = Math.max(0, remainingMs / 1000);
        setRemainingSeconds(Math.ceil(remainingSeconds));

        if (remainingMs <= 0) {
          clearTimers();
          setIsExpired(true);
        }
      }, COUNTDOWN_TICK_MS);
    }

    async function syncWithServer() {
      try {
        const data = await triggerServerStatusRef.current(null);
        const serverNowMs = new Date(data?.current_utc_time ?? '').getTime();

        if (Number.isNaN(serverNowMs)) {
          return Date.now() + serverOffsetRef.current;
        }

        const localNowMs = Date.now();

        const newOffset = serverNowMs - localNowMs;
        serverOffsetRef.current = newOffset;
        setServerOffset(newOffset);

        return serverNowMs;
      } catch {
        // On heartbeat failure: keep previous offset, retry on next cycle
        return Date.now() + serverOffsetRef.current;
      }
    }

    async function initTimer() {
      setIsInitializingTimer?.(true);

      // If startedAt is empty, start local countdown immediately while syncing with server
      let effectiveStartedAt = startedAt;
      let usingLocalClock = false;

      if (!startedAt) {
        effectiveStartedAt = new Date().toISOString();
        usingLocalClock = true;
      }

      // Calculate initial remaining (using local time initially)
      const initialRemaining = getRemainingSeconds(
        effectiveTimeSeconds,
        effectiveStartedAt,
        Date.now(),
        completionDeadlineDatetime,
        endDatetime,
      );

      if (initialRemaining <= 0) {
        setIsExpired(true);
        setIsInitializingTimer?.(false);
        return;
      }

      // Start countdown immediately with local estimate
      setRemainingSeconds(Math.ceil(initialRemaining));
      startCountdown(initialRemaining);

      // Sync with server in background to get accurate offset
      const serverNowMs = await syncWithServer();

      if (cancelled) {
        setIsInitializingTimer?.(false);
        return;
      }

      // If we generated startedAt from local clock, recalibrate with server time
      if (usingLocalClock) {
        effectiveStartedAt = new Date(serverNowMs).toISOString();

        // Recalculate with server time for accuracy
        const recalibratedRemaining = getRemainingSeconds(
          effectiveTimeSeconds,
          effectiveStartedAt,
          serverNowMs,
          completionDeadlineDatetime,
          endDatetime,
        );

        if (recalibratedRemaining <= 0) {
          clearTimers();
          setIsExpired(true);
          setIsInitializingTimer?.(false);
          return;
        }

        // Restart countdown with server-accurate value
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
        }
        setRemainingSeconds(Math.ceil(recalibratedRemaining));
        startCountdown(recalibratedRemaining);
      }

      setIsInitializingTimer?.(false);

      // Heartbeat every 10 minutes to recalibrate offset
      heartbeatRef.current = setInterval(async () => {
        const recalibratedNowMs = await syncWithServer();

        if (cancelled) return;

        const recalibratedRemaining = getRemainingSeconds(
          effectiveTimeSeconds,
          effectiveStartedAt,
          recalibratedNowMs,
          completionDeadlineDatetime,
          endDatetime,
        );

        if (recalibratedRemaining <= 0) {
          clearTimers();
          setIsExpired(true);
          return;
        }

        // Restart countdown with recalibrated value
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
        }
        startCountdown(recalibratedRemaining);
      }, HEARTBEAT_INTERVAL_MS);
    }

    if (isActive && effectiveTimeSeconds > 0) {
      initTimer();
    }

    return () => {
      cancelled = true;
      clearTimers();
    };
  }, [
    isActive,
    startedAt,
    effectiveTimeSeconds,
    completionDeadlineDatetime,
    endDatetime,
  ]);
}

export default useFocusModeTimer;
